export type StepFn = (dtSec: number) => void;
export type RenderFn = (alpha: number) => void;

/**
 * Источник Web Worker'а для генерации тиков симуляции. Воркер живёт в отдельном
 * потоке — `setInterval` внутри него НЕ throttle-ится при скрытой вкладке, так
 * что симуляция продолжается даже когда главный поток пасует RAF.
 *
 * Воркер только пингует, всю математику делает main thread в `onWorkerTick`.
 */
const WORKER_SOURCE = `
let id;
self.onmessage = (e) => {
  if (e.data && e.data.type === 'start') {
    if (id !== undefined) clearInterval(id);
    id = setInterval(function () { self.postMessage('tick'); }, e.data.intervalMs || 16);
  } else if (e.data && e.data.type === 'stop') {
    if (id !== undefined) { clearInterval(id); id = undefined; }
  }
};
`;

/**
 * Фиксированный таймстеп с разделением симуляции и рендера:
 * - **Симуляция** (input → state → network) тикается из Web Worker'а, поэтому
 *   продолжает идти на неактивной вкладке. На главном потоке принимаем
 *   сообщение и догоняем `step()` столько раз, сколько влезает в реальный dt.
 * - **Рендер** идёт через `requestAnimationFrame`. Когда вкладка скрыта,
 *   браузер останавливает RAF — это нормально, никто на чёрный канвас
 *   смотреть не будет. Симуляция при этом не теряется.
 *
 * Если воркер недоступен (CSP, очень старый браузер), падаем на старый
 * вариант: и симуляция, и рендер крутятся в одном RAF-цикле.
 */
export class GameLoop {
  readonly stepDt = 1 / 60;
  private acc = 0;
  private last = 0;
  private rafId = 0;
  private worker: Worker | null = null;
  private workerUrl: string | null = null;
  private running = false;

  constructor(private step: StepFn, private renderFn: RenderFn) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.acc = 0;

    try {
      const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
      this.workerUrl = URL.createObjectURL(blob);
      this.worker = new Worker(this.workerUrl);
      this.worker.onmessage = this.onWorkerTick;
      this.worker.postMessage({ type: 'start', intervalMs: Math.round(this.stepDt * 1000) });
    } catch (err) {
      // Никаких воркеров — fallback на RAF-only.
      // eslint-disable-next-line no-console
      console.warn('GameLoop: Web Worker unavailable, falling back to RAF-only:', err);
      this.worker = null;
    }

    this.rafId = requestAnimationFrame(this.renderTick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    if (this.worker) {
      try { this.worker.postMessage({ type: 'stop' }); } catch {}
      this.worker.terminate();
      this.worker = null;
    }
    if (this.workerUrl) {
      URL.revokeObjectURL(this.workerUrl);
      this.workerUrl = null;
    }
  }

  /** Тик симуляции по сообщению воркера. dt считаем по реальному времени. */
  private onWorkerTick = (): void => {
    if (!this.running) return;
    const now = performance.now();
    let frameDt = (now - this.last) / 1000;
    this.last = now;
    if (frameDt > 0.25) frameDt = 0.25; // защита от «прыжка» после очень длинной паузы
    this.acc += frameDt;
    while (this.acc >= this.stepDt) {
      this.step(this.stepDt);
      this.acc -= this.stepDt;
    }
  };

  /** RAF-кадр рендера. Когда вкладка скрыта — браузер ставит на паузу, мы это терпим. */
  private renderTick = (now: number): void => {
    if (!this.running) return;
    // Fallback-ветка: если воркер не поднялся, симуляция идёт здесь же — как в
    // оригинальном GameLoop'е до этой правки.
    if (!this.worker) {
      let frameDt = (now - this.last) / 1000;
      this.last = now;
      if (frameDt > 0.25) frameDt = 0.25;
      this.acc += frameDt;
      while (this.acc >= this.stepDt) {
        this.step(this.stepDt);
        this.acc -= this.stepDt;
      }
    }
    const alpha = this.acc / this.stepDt;
    this.renderFn(alpha);
    this.rafId = requestAnimationFrame(this.renderTick);
  };
}
